// Dependencies

import React, { Component } from 'react';

// Utils
import classnames from 'classnames';
import PropTypes from 'prop-types';

// Components
import { InputPercent, Tooltip, Text } from '@nerdwallet/currency';
import { creditScoreRanges } from '@nerdwallet/mortgage-utils/rate-adjustment';
import { Num, Tracking } from '@nerdwallet/nw-client-lib';

// CSS
require('./interest-rate-control.less');

// Constants
const { formInputChanged } = Tracking;

class InterestRateControl extends Component {
  constructor(props) {
    super(props);

    this.onUserInterestRateChange = this.onUserInterestRateChange.bind(this);

    this.useCreditScore = this.onInterestRateMethodChange.bind(this, true);
    this.useUserInterestRate = this.onInterestRateMethodChange.bind(
      this,
      false
    );
  }

  onInterestRateMethodChange(value) {
    this.trackInputChanged(
      'interet-rate-method',
      value ? 'credit-score' : 'user-interest-rate'
    );

    this.props.onChange(
      {
        key: 'inputs.useCreditScoreForInterestRate',
        inputId: 'annualInterestRate',
      },
      value
    );
  }

  onUserInterestRateChange(e) {
    // This conversion ensures that the annualInterestRate is constrained to a value with an exact amount of decimals.
    // With this logic we avoid side-effects originated by having a long float number (such as: 7.000000001) that filled the <InputPercent> control with zeros.
    const value = e.target.value
      ? parseFloat(Num.value(e.target.value).toFixed(6))
      : '';
    this.trackInputChanged('user-interest-rate', value);

    this.props.onChange(
      {
        key: 'inputs.annualInterestRate',
        inputId: 'annualInterestRate',
      },
      value
    );
  }

  onCreditScoreSelectClick(value) {
    this.trackInputChanged('credit-score-select', value);

    this.props.onChange(
      {
        key: 'inputs.creditScoreRangeValue',
        inputId: 'annualInterestRate',
      },
      value
    );
  }

  trackInputChanged(fieldName, fieldValue) {
    if (this.props.trackingFormName) {
      formInputChanged({
        formName: this.props.trackingFormName,
        fieldName,
        fieldValue: fieldValue ? fieldValue.toString() : '',
      });
    }
  }

  renderCreditScoreSelectButton(creditScoreRange) {
    const selected =
      creditScoreRange.value === this.props.creditScoreRangeValue;
    const onClick = this.onCreditScoreSelectClick.bind(
      this,
      creditScoreRange.value
    );

    return (
      <div
        className={classnames(
          'interest-rate-control-credit-score-select-option',
          { selected }
        )}
        onClick={onClick}
        key={creditScoreRange.value}
      >
        {selected ? (
          <div className="interest-rate-control-credit-score-select-option-min">
            {creditScoreRange.range[0]}
          </div>
        ) : null}
        <div className="interest-rate-control-credit-score-select-option-value">
          {creditScoreRange.text}
        </div>
        {selected ? (
          <div className="interest-rate-control-credit-score-select-option-max">
            {creditScoreRange.range[1]}
          </div>
        ) : null}
      </div>
    );
  }

  renderCreditScoreSelect() {
    const reversedCreditScoreRanges = [].concat(creditScoreRanges).reverse();

    return (
      <div className="interest-rate-control-credit-score-select">
        {reversedCreditScoreRanges.map((creditScoreRange) =>
          this.renderCreditScoreSelectButton(creditScoreRange)
        )}
      </div>
    );
  }

  formatInterestRateValue = (rate) => {
    // This conversion ensures that the formattedRate is constrained to a value with an exact amount of decimals needed by <InputPercent> component.
    // Example: annualInterestRate of 0.03022 is formatted to 3.022
    const formattedRate = rate ? parseFloat((rate * 100).toFixed(6)) : '';
    return formattedRate;
  };

  render() {
    const { useCreditScoreForInterestRate, annualInterestRate, validations } =
      this.props;
    const hasValidationError = validations?.annualInterestRate?.length;

    const label = useCreditScoreForInterestRate
      ? 'Interest rate by credit score'
      : 'Interest rate';

    const content = useCreditScoreForInterestRate
      ? "Credit score is a key factor in determining if you'll be able to get a mortgage and the rate you qualify for. Higher scores make you eligible for lower interest rates."
      : "The cost of borrowing money that's typically expressed as an annual percentage of the loan.";

    return (
      <div className="interest-rate-control">
        <div className="interest-rate-control-title">
          <Tooltip label={label} content={content} />
        </div>

        {useCreditScoreForInterestRate ? (
          <div className="interest-rate-control-method">
            {this.renderCreditScoreSelect()}

            <Text
              size="small"
              component="h3"
              className="interest-rate-control-switch"
            >
              Likely rate:
              <Text bold className="interest-rate-control-likely">
                {' '}
                {Num.formatPercent(annualInterestRate || 0)}{' '}
              </Text>
              <a onClick={this.useUserInterestRate}>Edit rate</a>
            </Text>
          </div>
        ) : (
          <div>
            <InputPercent
              numberOfDigits={2}
              decimalScale={3}
              onChange={this.onUserInterestRateChange}
              value={this.formatInterestRateValue(annualInterestRate)}
              error={hasValidationError}
              errorMessage={
                validations?.annualInterestRate?.length > 0 &&
                validations.annualInterestRate[0]
              }
            />
            <h3 className="interest-rate-control-switch">
              <a onClick={this.useCreditScore}>Interest rate by credit score</a>
            </h3>
          </div>
        )}
      </div>
    );
  }
}

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
InterestRateControl.displayName = 'InterestRateControl';

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
InterestRateControl.propTypes = {
  onChange: PropTypes.func.isRequired,
  annualInterestRate: PropTypes.number,
  useCreditScoreForInterestRate: PropTypes.bool.isRequired,
  creditScoreRangeValue: PropTypes.string.isRequired,
  trackingFormName: PropTypes.string.isRequired,
  validations: PropTypes.object.isRequired,
};

// eslint-disable-next-line react/static-property-placement
InterestRateControl.defaultProps = {
  annualInterestRate: 0,
};

export default InterestRateControl;
